import { useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { Search } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { EmailMessage } from '../../api/email'
import { useAuthStore } from '../../store/auth'

interface Props {
  messages: EmailMessage[]
  selectedId: string | null
  onSelect: (msg: EmailMessage) => void
  onSearch: (q: string) => void
  isLoading: boolean
}

export function MessageList({ messages, selectedId, onSelect, onSearch, isLoading }: Props) {
  const [search, setSearch] = useState('')
  const isSalesUser = useAuthStore(s => s.user?.role === 'sales_user')

  return (
    <div className="w-56 flex-shrink-0 border-r border-gray-200 flex flex-col">
      <div className="p-2 border-b border-gray-100">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input type="text" value={search}
            onChange={e => { setSearch(e.target.value); onSearch(e.target.value) }}
            placeholder="Search…"
            className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:border-indigo-400" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading && <div className="p-4 text-center text-xs text-gray-400">Loading…</div>}
        {!isLoading && messages.length === 0 && <div className="p-4 text-center text-xs text-gray-400">No messages</div>}
        {messages.map(msg => {
          const isUnread = msg.direction === 'received' && !msg.isRead
          return (
            <button key={msg.id} onClick={() => onSelect(msg)}
              className={cn('w-full text-left px-3 py-2.5 border-b border-gray-100 hover:bg-gray-50 transition-colors block',
                selectedId === msg.id && 'bg-indigo-50')}>
              <div className="flex items-center gap-1">
                {isUnread && <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />}
                <span className={cn('text-xs text-gray-800 truncate flex-1', isUnread && 'font-bold')}>
                  {isSalesUser
                    ? (msg.direction === 'received' ? 'Customer' : 'Sent')
                    : (msg.direction === 'received' ? msg.fromAddress : (msg.toAddresses[0] ?? '(no recipient)'))}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0 ml-1">
                  {formatDistanceToNow(new Date(msg.createdAt), { addSuffix: false })}
                </span>
              </div>
              <div className={cn('text-xs truncate mt-0.5', isUnread ? 'font-semibold text-gray-700' : 'text-gray-600')}>
                {msg.subject}
              </div>
              {msg.bodyText && <div className="text-xs text-gray-400 truncate mt-0.5">{msg.bodyText.slice(0, 60)}</div>}
            </button>
          )
        })}
      </div>
    </div>
  )
}
